import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  IconButton,
  LANGUAGE_ENGLISH_NAMES,
  makeStyles,
  TranslationStatusChip
} from '@paintscout/ui'
import type { DialogProps, DropdownSelectOption, SupportedLang } from '@paintscout/ui'
import type { TranslationCompletionStatus } from '@paintscout/util/builder/quote/util/translation-status'
import type { QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import { mergeLangStatus, useGenerateWorkOrderTranslation } from './useGenerateWorkOrderTranslation'

const useStyles = makeStyles((theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  alert: { marginTop: theme.spacing() },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing()
  }
}))

export interface DownloadWorkOrderDialogProps extends Omit<DialogProps, 'onConfirm'> {
  availableLangs: string[]
  /** The quote being downloaded; used to generate translations and as the base for the PDF. */
  quote: QuoteDocument
  companyId: string
  /**
   * Preselects the dropdown to whatever language the caller already had selected (e.g. the WO
   * dialog's own language menu) instead of always defaulting to English — otherwise a customer
   * who'd already picked Spanish there had to pick it again in this dialog (#22259).
   */
  initialLang?: string
  /**
   * Per-language pre-bake status. If the selected language is not `complete`, generation is
   * offered inline (you'd only ever generate before downloading) before the download is allowed.
   */
  translationStatus?: Record<string, TranslationCompletionStatus>
  /**
   * Called with the language code and the freshest quote document. When a translation is
   * generated in-dialog the mutation response already carries the persisted quote, so the
   * download reflects it without a page refresh or a separate re-fetch.
   */
  onConfirm: (lang: string | undefined, quote: QuoteDocument) => void
  onCancel: () => void
}

const ENGLISH_OPTION: DropdownSelectOption = {
  label: LANGUAGE_ENGLISH_NAMES['en'],
  value: 'en'
}

function DownloadWorkOrderDialog({
  availableLangs,
  quote,
  companyId,
  translationStatus = {},
  initialLang,
  onConfirm,
  onCancel,
  ...dialogProps
}: DownloadWorkOrderDialogProps) {
  const classes = useStyles()
  const generateWorkOrderTranslation = useGenerateWorkOrderTranslation()

  const langOptions: DropdownSelectOption[] = [
    ENGLISH_OPTION,
    ...availableLangs.map((lang) => ({
      label: LANGUAGE_ENGLISH_NAMES[lang as SupportedLang],
      value: lang
    }))
  ]

  const [selectedLang, setSelectedLang] = useState<DropdownSelectOption>(
    () => langOptions.find((option) => option.value === initialLang) ?? ENGLISH_OPTION
  )
  // Status starts from the prop, but is updated in place after an in-dialog generation so the
  // estimator can download right away rather than reopening or refreshing.
  const [status, setStatus] = useState<Record<string, TranslationCompletionStatus>>(translationStatus)
  // The quote handed to onConfirm — swapped for a freshly re-fetched copy once a translation is
  // generated so the downloaded PDF contains the new content.
  const [currentQuote, setCurrentQuote] = useState<QuoteDocument>(quote)
  const [generating, setGenerating] = useState(false)
  // Set once an in-dialog generation attempt for the selected language has failed, so we can
  // point the estimator to support instead of leaving them to guess why Download isn't working.
  // Cleared on language change since a fresh language hasn't failed yet.
  const [attemptFailed, setAttemptFailed] = useState(false)

  const lang = selectedLang.value
  const langLabel = LANGUAGE_ENGLISH_NAMES[lang as SupportedLang] ?? lang

  // English never needs generation; a non-English language must be pre-baked ('complete')
  // before its PDF is downloaded, otherwise the estimator could get untranslated content (or,
  // if generated live inside the ~15s render window, a silent English fallback — #20847).
  const needsGeneration = lang !== 'en' && status[lang] !== 'complete'

  // Title pill reflecting the selected language's state: "Generating…" while producing,
  // "<Lang> Generated" once ready, and "<Lang> Not Generated" (warning) when it still needs
  // generating. English never shows a chip.
  const chipStatus = generating
    ? 'generating'
    : lang === 'en'
      ? null
      : status[lang] === 'complete'
        ? 'translated'
        : status[lang] === 'partial'
          ? 'partial'
          : 'untranslated'

  // Single primary action: generation is tied into the download — if the selected language
  // isn't ready we generate it first, then download the freshly translated quote in the same
  // click. No separate "Generate" step or permanent block on retrying.
  async function handleDownload() {
    if (!needsGeneration) {
      onConfirm(lang === 'en' ? undefined : lang, currentQuote)
      return
    }

    setGenerating(true)
    try {
      const result = await generateWorkOrderTranslation({
        quote,
        companyId,
        lang,
        trigger: 'download-dialog',
        source: 'DownloadWorkOrderDialog'
      })
      setStatus((current) => mergeLangStatus(current, result.translationStatus))
      if (result.success && result.quote) {
        setAttemptFailed(false)
        setCurrentQuote(result.quote)
        onConfirm(lang, result.quote)
      } else {
        setAttemptFailed(true)
      }
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Dialog {...dialogProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={generating}>
            <CloseIcon />
          </IconButton>
        }
      >
        <span className={classes.titleRow}>
          Download Work Order
          {chipStatus && <TranslationStatusChip status={chipStatus} language={langLabel} />}
        </span>
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        <DropdownSelect
          variant="single"
          label="Language"
          options={langOptions}
          value={selectedLang}
          disabled={generating}
          onChange={(option) => {
            setAttemptFailed(false)
            setSelectedLang(option as DropdownSelectOption)
          }}
        />
        {generating && (
          <Alert severity="info" className={classes.alert}>
            Generating {langLabel} translation — this will download automatically once it&apos;s ready.
          </Alert>
        )}
        {!generating && needsGeneration && attemptFailed && (
          <Alert severity="warning" className={classes.alert}>
            {langLabel} translation is incomplete. Please{' '}
            <a href="mailto:support@paintscout.com">contact PaintScout Support</a> if retrying doesn&apos;t work.
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="text" onClick={onCancel} disabled={generating}>
            Cancel
          </Button>
        }
      >
        <Button variant="contained" color="primary" loading={generating} disabled={generating} onClick={handleDownload}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadWorkOrderDialog
